import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `personas:` scope (F16, `/personas`) — see
 * `apps/web/src/routes/settings/drive/drive.i18n.ts`'s doc comment for the
 * pattern this follows.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/personas.json`.
 */
const en = {
  'page.eyebrow': 'Author voices',
  'page.title': 'Personas',
  'tabs.mine': 'My personas',
  'tabs.public': 'Public',

  'list.action.edit': 'Edit',
  'list.action.delete': 'Delete',
  'list.action.import': 'Add to my personas',
  'list.empty.title': 'Create your first persona',
  'list.empty.description': 'Personas pre-fill voice, art style, and narration so your documents stay consistent.',
  'list.empty.action': 'Create your first persona',
  'list.empty.public.title': 'No public personas yet',
  'list.empty.public.description': 'When someone publishes a persona, it appears here.',
  'list.noResults.title': 'No personas match “{query}”',
  'list.noResults.description': 'Try a different search.',
  'list.error.title': "Couldn't load personas.",
  'list.searchLabel': 'Search personas',
  'list.searchPlaceholder': 'Search by name…',
  'visibility.public': 'Public',
  'visibility.private': 'Private',

  'action.new': 'New persona',
  'action.retry': 'Retry',
  'action.useInGeneration': 'Use in generation',

  'form.title.create': 'New persona',
  'form.title.edit': 'Edit persona',
  'form.title.import': 'Add to my personas',
  'form.name.label': 'Name',
  'form.name.placeholder': 'e.g. The Historian',
  'form.description.label': 'Description',
  'form.voice.label': 'Voice & tone',
  'form.voice.placeholder': 'Describe how this author writes…',
  'form.artStyle.label': 'Art style',
  'form.narrationSample.label': 'Narration sample',
  'form.narrationSample.placeholder': 'A representative opening line…',
  'form.isPublic.label': 'Make this persona public (others can use it)',
  'form.isPublic.hint': 'Public personas are moderated before they appear in Discover.',
  'form.action.save': 'Save persona',
  'form.action.create': 'Create persona',

  'delete.confirm.title': 'Delete',
  'delete.confirm.warning': 'Documents already generated with this persona keep their own snapshot and won’t change.',
  'delete.error': "Couldn't delete this persona. Try again.",

  'toast.created.action': 'Created',
  'toast.updated.action': 'Saved',
  'toast.deleted.action': 'Deleted',
  'toast.imported.action': 'Added',
  'toast.saveFailed': "Couldn't save this persona. Try again.",

  'preview.public': 'Public',
} as const;

const ro: Record<keyof typeof en, string> = {
  'page.eyebrow': 'Voci de autor',
  'page.title': 'Personaje',
  'tabs.mine': 'Personajele mele',
  'tabs.public': 'Publice',

  'list.action.edit': 'Editează',
  'list.action.delete': 'Șterge',
  'list.action.import': 'Adaugă la personajele mele',
  'list.empty.title': 'Creează primul tău personaj',
  'list.empty.description': 'Personajele pre-completează vocea, stilul artistic și naratiunea, astfel încât documentele tale să rămână consecvente.',
  'list.empty.action': 'Creează primul tău personaj',
  'list.empty.public.title': 'Niciun personaj public încă',
  'list.empty.public.description': 'Când cineva publică un personaj, apare aici.',
  'list.noResults.title': 'Niciun personaj nu se potrivește cu „{query}”',
  'list.noResults.description': 'Încearcă o altă căutare.',
  'list.error.title': 'Nu am putut încărca personajele.',
  'list.searchLabel': 'Caută personaje',
  'list.searchPlaceholder': 'Caută după nume…',
  'visibility.public': 'Public',
  'visibility.private': 'Privat',

  'action.new': 'Personaj nou',
  'action.retry': 'Reîncearcă',
  'action.useInGeneration': 'Folosește la generare',

  'form.title.create': 'Personaj nou',
  'form.title.edit': 'Editează personajul',
  'form.title.import': 'Adaugă la personajele mele',
  'form.name.label': 'Nume',
  'form.name.placeholder': 'ex. Istoricul',
  'form.description.label': 'Descriere',
  'form.voice.label': 'Voce și ton',
  'form.voice.placeholder': 'Descrie cum scrie acest autor…',
  'form.artStyle.label': 'Stil artistic',
  'form.narrationSample.label': 'Exemplu de naratiune',
  'form.narrationSample.placeholder': 'O replică de deschidere reprezentativă…',
  'form.isPublic.label': 'Fă acest personaj public (alții îl pot folosi)',
  'form.isPublic.hint': 'Personajele publice sunt moderate înainte de a apărea în Descoperă.',
  'form.action.save': 'Salvează personajul',
  'form.action.create': 'Creează personajul',

  'delete.confirm.title': 'Șterge',
  'delete.confirm.warning': 'Documentele deja generate cu acest personaj își păstrează propria variantă și nu se vor schimba.',
  'delete.error': 'Nu am putut șterge acest personaj. Încearcă din nou.',

  'toast.created.action': 'Creat',
  'toast.updated.action': 'Salvat',
  'toast.deleted.action': 'Șters',
  'toast.imported.action': 'Adăugat',
  'toast.saveFailed': 'Nu am putut salva acest personaj. Încearcă din nou.',

  'preview.public': 'Public',
};

export const PERSONAS_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type PersonasCatalogKey = keyof typeof en;
